import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { adminAuthService, AdminUser } from '@/services/auth';
import { clearAuthToken, setAuthToken } from '@/lib/api';

interface AuthContextType {
  user: AdminUser | null;
  loading: boolean;
  login: (token: string, user: AdminUser) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
  refreshUser: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('roasform_backoffice_token');
      if (token) {
        try {
          const profile = await adminAuthService.getMe();
          setUser(profile);
        } catch (error) {
          clearAuthToken();
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = (token: string, profile: AdminUser) => {
    setAuthToken(token);
    setUser(profile);
  };

  const logout = async () => {
    setLoading(true);
    await adminAuthService.logout();
    clearAuthToken();
    setUser(null);
    setLoading(false);
  };

  const refreshUser = async () => {
    try {
      const profile = await adminAuthService.getMe();
      setUser(profile);
    } catch (error) {
      console.error("Failed to refresh user", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
